import React from "react";
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { QuickRecordNewPatientInput } from "./quickRecordService";

type QuickRecordCreatePatientFormProps = {
  value: QuickRecordNewPatientInput;
  showOptionalFields: boolean;
  onChange: (next: QuickRecordNewPatientInput) => void;
  onToggleOptionalFields: () => void;
  onBackToSearch: () => void;
};

export const QuickRecordCreatePatientForm: React.FC<
  QuickRecordCreatePatientFormProps
> = ({
  value,
  showOptionalFields,
  onChange,
  onToggleOptionalFields,
  onBackToSearch,
}) => {
  return (
    <View style={styles.container}>
      <Text style={styles.title}>Add patient (minimum required)</Text>

      <View style={styles.row}>
        <View style={styles.field}>
          <Text style={styles.label}>First name *</Text>
          <TextInput
            style={styles.input}
            value={value.firstName}
            onChangeText={(firstName) => onChange({ ...value, firstName })}
            placeholder="First name"
            placeholderTextColor="#8e96a3"
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>Last name *</Text>
          <TextInput
            style={styles.input}
            value={value.lastName}
            onChangeText={(lastName) => onChange({ ...value, lastName })}
            placeholder="Last name"
            placeholderTextColor="#8e96a3"
          />
        </View>
      </View>

      <TouchableOpacity
        style={styles.optionalHeader}
        onPress={onToggleOptionalFields}
      >
        <Text style={styles.optionalTitle}>Optional details</Text>
        {showOptionalFields ? (
          <ChevronUp size={16} color="#4b5563" />
        ) : (
          <ChevronDown size={16} color="#4b5563" />
        )}
      </TouchableOpacity>

      {showOptionalFields && (
        <View style={styles.optionalFields}>
          <TextInput
            style={styles.input}
            value={value.email || ""}
            onChangeText={(email) => onChange({ ...value, email })}
            placeholder="Email (optional)"
            placeholderTextColor="#8e96a3"
            keyboardType="email-address"
            autoCapitalize="none"
          />
          <TextInput
            style={styles.input}
            value={value.phone || ""}
            onChangeText={(phone) => onChange({ ...value, phone })}
            placeholder="Phone (optional)"
            placeholderTextColor="#8e96a3"
            keyboardType="phone-pad"
          />
          <TextInput
            style={styles.input}
            value={value.dateOfBirth || ""}
            onChangeText={(dateOfBirth) => onChange({ ...value, dateOfBirth })}
            placeholder="DOB YYYY-MM-DD (optional)"
            placeholderTextColor="#8e96a3"
          />
          <TextInput
            style={styles.input}
            value={value.sex || ""}
            onChangeText={(sex) => onChange({ ...value, sex })}
            placeholder="Sex (optional)"
            placeholderTextColor="#8e96a3"
          />
        </View>
      )}

      <TouchableOpacity onPress={onBackToSearch}>
        <Text style={styles.backLink}>Back to patient search</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1e2733",
  },
  row: {
    flexDirection: "row",
    gap: 10,
  },
  field: {
    flex: 1,
    gap: 6,
  },
  label: {
    fontSize: 12,
    color: "#4b5563",
    fontWeight: "600",
  },
  input: {
    borderWidth: 1,
    borderColor: "#d5dce5",
    borderRadius: 10,
    backgroundColor: "#ffffff",
    paddingHorizontal: 12,
    height: 42,
    color: "#1e2733",
  },
  optionalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "#d5dce5",
    borderRadius: 10,
    backgroundColor: "#f7f9fc",
  },
  optionalTitle: {
    fontSize: 13,
    color: "#374151",
    fontWeight: "600",
  },
  optionalFields: {
    gap: 8,
  },
  backLink: {
    color: "#0066cc",
    fontSize: 13,
    fontWeight: "600",
  },
});
